import { T } from '@transifex/react';
import React, { useMemo, useState } from 'react';
import { pick } from 'lodash';
import { create } from 'zustand';
import dayjs from 'dayjs';
import { LoginShell } from 'src/layouts/LoginShell';
import { CitizenProvider } from 'src/context/citizen-context';
import { apiClient, apiClientMethods } from 'src/api/client';
import {
  clearHaulerSession,
  getHaulerEmail,
  isDevBypassAuth,
  isHaulerSession,
} from 'src/api/state';
import { CriptioLogin } from 'src/components/CriptioLogin';
import { EmailOtpLogin } from 'src/components/hauler-login/EmailOtpLogin';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { LoadingPage } from '../components/shared';
import { useCompany } from './Company.store';

const isHaulerPath = () =>
  typeof window !== 'undefined' &&
  window.location.pathname.startsWith('/hauler');

type TValue = {
  id: string;
  username: string;
  token: string;
  language: string;
  phone_number: string;
  email: string;
  name: string;
  anonymous: boolean;
  user_type?: 'citizen' | 'hauler';
};

type TStore = {
  data: TValue;
  logout: () => void;
  update: (v: Pick<TValue, 'language'>) => Promise<void>;
};

export const useUserStore = create<TStore>()(() => ({}) as TStore);

// Dev-only escape hatch: when the bypass flag is on (env var in dev or the
// localStorage toggle from the dev pill), force the user to appear
// non-anonymous so the app skips the MitID gate and falls through to the
// address-select + main app. Useful when you don't have a MitID to test
// tenants that are configured with login_method: 'user'.
const applyDevBypass = (data: TValue): TValue => {
  if (!isDevBypassAuth()) return data;
  return {
    ...data,
    anonymous: false,
    // When the bypass is active and the user lands on a /hauler URL, force the
    // role so the hauler route tree's role gate passes without a real backend
    // user_type. Lets developers exercise the hauler UI locally.
    ...(isHaulerPath() ? { user_type: 'hauler' as const } : {}),
  };
};

const applyHaulerOverride = (data: TValue): TValue => {
  if (!isHaulerSession()) return data;
  const haulerEmail = getHaulerEmail() || data.email || '';
  return {
    ...data,
    anonymous: false,
    user_type: 'hauler',
    email: haulerEmail,
    name: haulerEmail ? haulerEmail.split('@')[0] : data.name,
  };
};

const augmentUser = (data: TValue): TValue =>
  applyHaulerOverride(applyDevBypass(data));

export const useUser = () => {
  return useUserStore();
};

export const useUserMaybe = (): TValue | undefined => {
  return useUserStore().data;
};

/*
- getToken: from AUTH_TOKEN or from search param or from the api.
- getUser: using the token

- If LOGIN_METHOD_NONE:
  - set property user and OK.
- If LOGIN_METHOD_MIDID_POST_SEARCH:
  - If CheckIfPropertyUserIsAnanymous is true then redirect to mit id.
  - else set property user and OK.
- If LOGIN_METHOD_MIDID_PRE_SEARCH
  - set property user and OK.

*/

function getErrorJsx(error: {
  errorCode?: string;
  errorMessage?: string;
}): React.ReactNode {
  if (error?.errorCode === 'NO_PROPERTY_SELECTED') {
    return (
      <T _str="No property was selected. Please go back and select a property before logging in." />
    );
  }
  if (error?.errorCode === 'USER_NOT_REGISTERED') {
    return (
      <T _str="We could not verify your identity for this property. Please try again, or contact your municipality if the issue persists." />
    );
  }
  if (error?.errorCode === 'INACTIVE_CPR') {
    return (
      <T _str="Your CPR number is not active. Please contact your municipality if you believe this is an error." />
    );
  }
  if (error?.errorMessage) {
    return error.errorMessage;
  }
  return <T _str="An unknown error occurred" />;
}

export const UserStoreProvider = ({
  children,
}: {
  children: JSX.Element;
}): JSX.Element => {
  const { data: company, hostname } = useCompany();
  const { data: user } = useUser();
  const searchParams = useMemo(
    () => new URLSearchParams(window.location.search),
    []
  );
  const [error, setError] = useState<{
    errorCode?: string;
    errorMessage?: string;
  }>({});

  const logout = () => {
    const wasHauler = isHaulerSession();
    localStorage.setItem('AUTH_TOKEN', '');
    localStorage.setItem('AUTH_PROPERTY_ID', '');
    clearHaulerSession();
    window.history.replaceState(
      {},
      document.title,
      wasHauler ? '/hauler' : window.WH_SS_INIT_ROUTE || '/'
    );
    window.location.reload();
  };

  const update: TStore['update'] = async (values) => {
    await apiClient
      .post(`property_user/`, {
        ...pick(user, ['name', 'phone_number', 'email', 'language']),
        ...values,
      })
      .then((d) => {
        useUserStore.setState({ data: d.data });
      });
  };

  const { isLoading } = useQuery({
    queryKey: ['UserStoreProvider', 'Get'],
    queryFn: async () => {
      // if there is error then show error and return
      const errorCode = searchParams.get('error_code') || undefined;
      const errorMessage = searchParams.get('error') || undefined;
      if (errorCode || errorMessage) {
        setError({
          errorCode,
          errorMessage,
        });
        return null;
      }

      // get the token
      const token =
        searchParams.get('api_key') || localStorage.getItem('AUTH_TOKEN');

      if (token) {
        // need to remove the api_key from the url once we used it.
        if (searchParams.get('api_key')) {
          const propertyId = searchParams.get('property_key');
          // also checking for property key here
          if (propertyId) {
            localStorage.setItem('AUTH_PROPERTY_ID', propertyId as string);
            localStorage.setItem(
              'AUTH_TOKEN_EXPIRY',
              dayjs().add(60, 'minutes').toISOString()
            );
          }
          window.history.replaceState({}, document.title, '/');
        }

        apiClientMethods.setToken(token);
        return apiClient
          .get('property_user/')
          .then((d) => {
            if (d.data.id) {
              useUserStore.setState({
                data: augmentUser(d.data),
                logout,
                update,
              });
              localStorage.setItem('AUTH_TOKEN', token);
            }
            return null;
          })
          .catch((e) => {
            setError({
              errorMessage: e?.message || <T _str="Something went wrong" />,
            });
            return null;
          });
      }
      // On /hauler routes, skip the empty-cred auto-login until the user has
      // either passed the OTP gate (HAULER_MODE) or enabled the dev bypass.
      // Once one of those is set, fall through to the same anonymous tenant
      // login the citizen flow uses — the resulting token has tenant-scoped
      // read access (same one AddressSelect uses pre-MitID), which is what
      // lets the hauler portal pull real properties + containers.
      if (isHaulerPath() && !isDevBypassAuth() && !isHaulerSession()) {
        return null;
      }
      // if there is no token then fetch it using company login method.
      return apiClient
        .post(`company/${hostname}/login`, { username: '', password: '' })
        .then((d) => {
          if (d.data.id) {
            apiClientMethods.setToken((d.data as TValue).token);
            useUserStore.setState({
              data: augmentUser(d.data),
              logout,
              update,
            });
            localStorage.setItem('AUTH_TOKEN', (d.data as TValue).token);
          }
          return null;
        })
        .catch((e) => {
          if (e.response.status === 404) {
            // wrong property selected. Not authorized. invalid token
            logout();
          }
          return null;
        });
    },
  });

  if (isLoading) {
    return <LoadingPage />;
  }

  const onHaulerPath = isHaulerPath();

  // On /hauler, only a user with user_type === 'hauler' is considered
  // authenticated. A stale citizen token shouldn't grant access — show the
  // OTP login instead so the user can sign in as a hauler explicitly.
  if (onHaulerPath && (!user || user.user_type !== 'hauler')) {
    if (!error.errorCode && !error.errorMessage) {
      return (
        <LoginShell title={<T _str="Sign in to your hauler account" />}>
          <EmailOtpLogin />
        </LoginShell>
      );
    }
  }

  if (user) {
    if (
      !onHaulerPath &&
      (company.login_method === 'user' || company.login_method === 'bankid') &&
      company.login_method_user === 'post_auth_search' &&
      user.anonymous
    ) {
      // show mit id login here.
      return (
        <LoginShell>
          <CriptioLogin />
        </LoginShell>
      );
    }
    // Hauler routes don't run the CitizenProvider — haulers aren't bound to a
    // single property like citizens are.
    if (onHaulerPath) {
      return children;
    }
    return <CitizenProvider>{children}</CitizenProvider>;
  }

  return (
    <LoginShell>
      <div className="flex w-full flex-col items-center gap-5 rounded-lg bg-secondary p-10 text-center">
        <p className="text-base text-muted-foreground">{getErrorJsx(error)}</p>
        <Button onClick={logout}>
          <T _str="Back to Login" />
        </Button>
      </div>
    </LoginShell>
  );
};
